import { useState } from 'react';

import env from 'env';
import i18next from 'i18next';

import { notification } from '@/app/core/notifications/notification';

import { resolveAuthReturnUrl } from '../../../../core/security/returnUrl';
import Service from '../../../../core/security/service';
import EnviromentVariablesService from '../../../Parameters/EnvironmentVariable/enviroment.service';
import { type LoginRequest } from '../../models/login.model';

export const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const [usernameIsValid, setUsernameIsValid] = useState(false);
  const [validateCaptcha, setValidateCaptcha] = useState(false);
  const [externalWsLoginEnabled, setExternalWsLoginEnabled] = useState(false);
  const [externalWsLoginSelected, setExternalWsLoginSelected] = useState(false);

  const loginUserName = async (username: string, returnUrl: string) => {
    setLoading(true);
    try {
      const result = await Service.loginUserName({
        userName: username,
        returnUrl,
        externalWsLoginSelected
      });

      if (result.response) {
        setUsernameIsValid(true);
        setValidateCaptcha(result.response.validateCaptcha);
      }
    } catch (error) {
      console.error('Login username error:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCaptchaRequirement = async (username: string, returnUrl: string) => {
    const result = await Service.loginUserName({
      userName: username,
      returnUrl,
      externalWsLoginSelected
    });

    if (result.response) {
      setValidateCaptcha(result.response.validateCaptcha);
    }
  };

  const login = async (
    data: LoginRequest & {
      DNTCaptchaInputText?: string;
      DNTCaptchaText?: string;
      DNTCaptchaToken?: string;
    }
  ) => {
    // Validar captcha si es necesario
    if (validateCaptcha && !data.DNTCaptchaInputText) {
      notification.error(i18next.t('security-login.validations.captchaValidationRequired'));
      return;
    }

    setLoading(true);
    try {
      let result;

      if (!externalWsLoginSelected) {
        result = await Service.login({
          username: data.username,
          password: data.password,
          rememberLogin: data.rememberLogin,
          DNTCaptchaInputText: data.DNTCaptchaInputText,
          DNTCaptchaText: data.DNTCaptchaText,
          DNTCaptchaToken: data.DNTCaptchaToken,
          returnUrl: data.returnUrl
        });
      } else {
        result = await Service.loginExternalWs({
          username: data.username,
          password: data.password,
          DNTCaptchaInputText: data.DNTCaptchaInputText,
          DNTCaptchaText: data.DNTCaptchaText,
          DNTCaptchaToken: data.DNTCaptchaToken,
          returnUrl: data.returnUrl
        });
      }

      if (result.response) {
        if (!result.response.redirect2FA) {
          window.location.href = resolveAuthReturnUrl(result.response.returnUrl);
        } else {
          window.location.href = `${env.appUrl}/loginWith2fa?returnUrl=${escape(
            result.response.returnUrl
          )}&rememberMe=${data.rememberLogin ? 1 : 0}`;
        }
      } else {
        await refreshCaptchaRequirement(data.username, data.returnUrl);
      }
    } catch (error) {
      console.error('Login error:', error);
      await refreshCaptchaRequirement(data.username, data.returnUrl);
    } finally {
      setLoading(false);
    }
  };

  const loadExternalWsLoginEnabled = async () => {
    try {
      const result = await EnviromentVariablesService.getExternalWsLoginEnabled();

      if (result.response) {
        const enabled = result.response?.textValue?.toLowerCase() === 'true';
        setExternalWsLoginEnabled(enabled);
      }
    } catch (error) {
      console.error('Load external WS login enabled error:', error);
    }
  };

  const resetUsername = () => {
    setUsernameIsValid(false);
    setValidateCaptcha(false);
  };

  return {
    loading,
    usernameIsValid,
    validateCaptcha,
    externalWsLoginEnabled,
    externalWsLoginSelected,
    loginUserName,
    login,
    loadExternalWsLoginEnabled,
    resetUsername,
    setExternalWsLoginSelected
  };
};
